<script setup lang="ts">
  import { Select } from 'ant-design-vue'
  import { TableDataSourceType } from '../models/TableDataSourceType'
  import { TableSchema } from '../models/TableSchema'
  import { TableField } from '../models/TableField'
  import { nextTick, onMounted, ref, watch } from 'vue'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { useFileCacheStore } from '@/composables/useFileCacheStore'
  import Papa from 'papaparse'
  import { AnalysisWizardService } from '@/components/AnalysisWizard/services/AnalysisWizardService'
  import { toast } from 'vue-sonner'
  import { AnalysisWizardModel } from '@/components/AnalysisWizard/models/AnalysisWizardModel'

  export interface ITableColumnTypeConfiguration {
    tableSchemas: Array<TableSchema>
    modelValue: string
    configuration: any
    configuredField: TableField

    selectedDataSource?: { value: string; label: string; id: string; type: TableDataSourceType }
    configurationLevel: number
    isCondensedLayout: boolean
    globalCollapsed?: { collapsed: boolean }
    wizardModel?: AnalysisWizardModel
  }

  const fileCache = useFileCacheStore()
  const pivotColumnType = 'pivot'
  const props = defineProps<ITableColumnTypeConfiguration>()

  const selectedValue = ref<string>(props.modelValue)

  const emit = defineEmits<{
    'update:modelValue': [value: string]
    'generating-rows': [isGeneratingRows: boolean]
    'row-generation-complete': [success: boolean]
    'configuration-updated': [config: Record<string, any>]
  }>()

  watch(
    () => props.modelValue,
    (newVal) => {
      selectedValue.value = newVal
    }
  )

  watch(selectedValue, async () => {
    // first update parent model so props.modelValue is in sync
    emit('update:modelValue', selectedValue.value)

    if (selectedValue.value !== pivotColumnType) {
      const newConfig = { ...(props.configuration || {}) }
      delete newConfig['pivotColumn']
      delete newConfig['pivotValueColumn']
      delete newConfig['columns']
      // defer emit to avoid sync watcher recursion
      nextTick(() => emit('configuration-updated', newConfig))
      return
    }

    // delay generation until parent has applied modelValue & any prop-driven changes settle
    await nextTick()
    // small extra defer to ensure parent handlers that react to modelValue finished
    await new Promise((r) => setTimeout(r, 0))

    // Only (re)-generate columns if they don't exist
    if (props.configuration?.columns == null) {
      onColumnTypeChanged(selectedValue.value)
    }
  })

  const onColumnTypeChanged = async (columnType: string) => {
    const pivotKey = props.configuration?.pivotColumn
    if (columnType === pivotColumnType && pivotKey) {
      const rowLimit: number = 50

      if (props.selectedDataSource?.id == null && props.selectedDataSource?.type == null)
        throw Error('Missing type or id trying to generate columns for selectedDataSource!')

      const fileContent = fileCache.getBlob(
        props.selectedDataSource.type,
        props.selectedDataSource.id
      )
      if (!fileContent) return

      // Notify immediate start
      emit('generating-rows', true)

      try {
        const csvText = await fileContent.text()

        // run the CPU work through the shared helper so the task runs to completion
        // even if this component is toggled in the UI; still await it here so we
        // emit configuration-updated only after parsing finishes.
        await AnalysisWizardService.runAsyncTaskWithLoading(
          'Generating pivot columns...',
          props.wizardModel,
          async () => {
            // awaitable wrapper for Papa.parse
            const results = await new Promise<any>((resolve, reject) => {
              Papa.parse(csvText, {
                header: true,
                skipEmptyLines: true,
                dynamicTyping: true,
                // set worker:false to parse on main thread synchronously (promise still awaited)
                // set worker:true to parse in a worker — promise resolves on complete either way
                worker: true,
                complete: (r) => resolve(r),
                error: (err: any) => reject(err)
              })
            })

            const fileRows = results.data as Array<Record<string, any>>
            const pivotKeyLocal = props.configuration?.pivotColumn
            // collect unique, non-empty values
            const pivotOptions = Array.from(
              new Set(
                fileRows
                  .map((r) => r[pivotKeyLocal])
                  .filter((v) => v != null && String(v).trim() !== '')
              )
            )
            // determine if all values are numeric (coercible to finite numbers)
            const allNumeric = pivotOptions.length > 0 && pivotOptions.every((v) => {
              const s = String(v).trim()
              if (s === '') return false
              const n = Number(s)
              return Number.isFinite(n)
            })
            // sort: numeric ascending when all numeric, otherwise natural/alphanumeric order
            if (allNumeric) {
              pivotOptions.sort((a, b) => Number(String(a)) - Number(String(b)))
            } else {
              pivotOptions.sort((a, b) =>
                String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
              )
            }

            const limitedPivotOptions = pivotOptions.slice(0, rowLimit)
            const newColumns = limitedPivotOptions.map((value: any) => ({
              name: String(value),
              label: String(value),
              type: 'statistical',
              display: true
            }))

            const updatedColumns = [...newColumns]
            const newConfig = { ...(props.configuration || {}), columns: updatedColumns }

            // avoid no-op emits that can trigger watchers unnecessarily
            if (JSON.stringify(updatedColumns) !== JSON.stringify(props.configuration?.columns)) {
              // schedule emit to avoid synchronous watcher recursion
              nextTick(() => {
                emit('configuration-updated', newConfig)
                emit('row-generation-complete', true)
              })
            }
          }
        )
      } catch (err: any) {
        toast.error(`Error generating pivot columns: ${err?.message ?? err}`)
        emit('row-generation-complete', false)
      } finally {
        // Emit finished regardless of component being toggled; parent should receive this
        // (the parsing work was awaited above via AnalysisWizardService)
        emit('generating-rows', false)
      }
    } else {
      // remove pivot-specific keys and emit once (avoid mutating props)
      const newConfig = { ...(props.configuration || {}) }
      delete newConfig['pivotColumn']
      delete newConfig['pivotValueColumn']
      delete newConfig['columns']

      // only emit if there is a real change to avoid watcher loops
      if (JSON.stringify(newConfig) !== JSON.stringify(props.configuration)) {
        // schedule to break sync watcher cycles
        nextTick(() => {
          emit('configuration-updated', newConfig)
          emit('row-generation-complete', true)
        })
      } else {
        nextTick(() => emit('row-generation-complete', true))
      }
    }
  }

  const mounted = ref<boolean>(false)

  onMounted(() => {
    // Only run onColumnTypeChanged when pivotColumn actually changes after its initial set.
    // Skip the transition from undefined -> value (first set).
    watch(
      () => props.configuration?.pivotColumn,
      (newPivot) => {
        if (newPivot != null && mounted.value) {
          onColumnTypeChanged(selectedValue.value)
        }
        mounted.value = true
      },
      { immediate: true }
    )
  })

  const { controlSize } = useWindowSize()
</script>

<template>
  <!-- Param or Column Select -->
  <Select
    v-model:value="selectedValue"
    :placeholder="props.configuredField?.placeholder ?? 'Select Type'"
    :size="controlSize"
    class="w-full"
  >
    <SelectOption
      v-for="option in configuredField.options"
      :key="option.value"
      :value="option.value"
    >
      {{ option.label }}
    </SelectOption>
  </Select>
</template>
